import type { ReactElement } from 'react';

type TrackTileProps = {
  readonly title: string;
  readonly colour: string | undefined;
  readonly size?: number;
  readonly imageUrl?: string | null;
};

const DEFAULT_SIZE = 44;
const PATTERN_COUNT = 5;

const hashOf = (value: string): number =>
  Array.from({ length: value.length }, (_, index) => value.codePointAt(index) ?? 0).reduce(
    (hash, code) => (hash * 31 + code) % 9973,
    11,
  );

// Stand-in cover art for the mock catalogue, which has no real artwork: a
// deterministic abstract pattern per track instead of a broken <img>.
export const TrackTile = ({
  title,
  colour,
  size = DEFAULT_SIZE,
  imageUrl,
}: TrackTileProps): ReactElement => {
  if (imageUrl !== undefined && imageUrl !== null) {
    return (
      <img
        src={imageUrl}
        alt=""
        className="border-subtle shrink-0 rounded-control border object-cover"
        style={{ width: size, height: size }}
      />
    );
  }

  const hash = hashOf(title);
  const ink = colour ?? '#404040';
  const pattern = hash % PATTERN_COUNT;

  const shapes = (): ReactElement => {
    if (pattern === 0) {
      const bars = Array.from({ length: 5 }, (_, i) => i).filter((i) => (hash + i) % 2 === 0);
      return (
        <>
          {bars.map((i) => (
            <rect key={i} x={0} y={(i * size) / 5} width={size} height={size / 5} fill={ink} />
          ))}
        </>
      );
    }
    if (pattern === 1) {
      const rings = [0.45, 0.32, 0.19, 0.06];
      return (
        <>
          {rings.map((r, i) => (
            <circle
              key={r}
              cx={size / 2}
              cy={size / 2}
              r={r * size}
              fill={i % 2 === 0 ? 'none' : ink}
              stroke={ink}
              strokeWidth={i % 2 === 0 ? size * 0.07 : 0}
            />
          ))}
        </>
      );
    }
    if (pattern === 2) {
      const bars = Array.from({ length: 4 }, (_, i) => i).filter((i) => (hash + i) % 2 === 0);
      return (
        <>
          {bars.map((i) => (
            <rect key={i} x={(i * size) / 4} y={0} width={size / 4} height={size} fill={ink} />
          ))}
        </>
      );
    }
    if (pattern === 3) {
      const cells = Array.from({ length: 4 }, (_, row) => row).flatMap((row) =>
        Array.from({ length: 4 }, (_, col) => ({ row, col })),
      );
      return (
        <>
          {cells
            .filter(({ row, col }) => (hash + row * 4 + col) % 3 !== 0)
            .map(({ row, col }) => (
              <circle
                key={`${String(row)}-${String(col)}`}
                cx={(col + 0.5) * (size / 4)}
                cy={(row + 0.5) * (size / 4)}
                r={size * 0.07}
                fill={ink}
              />
            ))}
        </>
      );
    }
    return (
      <>
        <rect x={0} y={0} width={size} height={size} fill={ink} opacity={0.18} />
        <path
          d={`M ${String(size)} 0 A ${String(size)} ${String(size)} 0 0 1 0 ${String(size)} L ${String(size)} ${String(size)} Z`}
          fill={ink}
        />
      </>
    );
  };

  return (
    <div
      aria-hidden="true"
      className="border-subtle shrink-0 overflow-hidden rounded-control border"
      style={{ width: size, height: size, backgroundColor: '#f4f4f0' }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${String(size)} ${String(size)}`}>
        {shapes()}
      </svg>
    </div>
  );
};
